import React, { useState } from 'react';
import { Trash2, Users } from 'lucide-react';
import { usePlayerDeletion } from '../../contexts/PlayerDeletionContext';
import { Player } from '../../types';

interface PlayerDeletionProps {
  player?: Player;
  tournamentId: string;
  isAdmin: boolean;
}

export const PlayerDeletion = ({ player, tournamentId, isAdmin }: PlayerDeletionProps) => {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { deletePlayer, deleteAllPlayers, isDeleting, error } = usePlayerDeletion();

  if (!isAdmin) return null;

  const handleDelete = async () => {
    try {
      if (player) {
        await deletePlayer(player.id, tournamentId);
      } else {
        await deleteAllPlayers(tournamentId);
      }
      setShowConfirmation(false);
    } catch (error) {
      // Error is handled by context
      console.error('Delete operation failed:', error);
    }
  };

  return (
    <>
      <button
        onClick={() => setShowConfirmation(true)}
        className="text-red-600 hover:text-red-700 flex items-center gap-2"
        disabled={isDeleting}
      >
        {player ? (
          <Trash2 className="w-4 h-4" />
        ) : (
          <>
            <Users className="w-4 h-4" />
            Delete All Players
          </>
        )}
      </button>

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm mx-4">
            <h3 className="text-lg font-semibold mb-2">
              {player ? 'Delete Player' : 'Delete All Players'}
            </h3>
            <p className="text-gray-600 mb-4">
              {player 
                ? 'Are you sure you want to remove this player?' 
                : 'This will remove all players from the tournament. This action cannot be undone.'}
            </p>
            {error && (
              <p className="text-sm text-red-600 mb-4">{error}</p>
            )}
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowConfirmation(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};